import {
  Canvas,
  Circle,
  FabricImage,
  Line,
  Polygon,
  type TPointerEventInfo,
  type XY,
} from "future-fabric";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

// --- Inlined constants (from legacy constants/shape) ---
const CROP_CANVAS_CORNER_SIZE = 10;
const CROP_CANVAS_MOUSE_BUFFER = 8;
const CROP_CANVAS_RESIZER_WIDTH = 2;

const CROP_CANVAS_POINT_TYPES = {
  ltr: 1,
  trb: 2,
  lrb: 3,
  ltb: 4,
  ltrb: 5,
  rb: 6,
  lb: 7,
  tr: 8,
  lt: 9,
} as const;

// --- Inlined shapes (from legacy shapes) ---

class SplitLine extends Line {
  direction: "vertical" | "horizontal";

  constructor(coords: [number, number, number, number], direction: "vertical" | "horizontal") {
    let [x1, y1, x2, y2] = coords;
    if (x1 > x2) [x1, x2] = [x2, x1];
    if (y1 > y2) [y1, y2] = [y2, y1];

    super([x1, y1, x2, y2], {
      fill: "red",
      stroke: "red",
      strokeWidth: 2,
      selectable: false,
      evented: false,
    });
    this.direction = direction;
  }
}

class PointerCircle extends Circle {
  constructor(top: number, left: number) {
    super({
      top,
      left,
      strokeWidth: 1,
      radius: 4,
      fill: "#ffffff",
      stroke: "red",
      selectable: false,
      originX: "center",
      originY: "center",
    });
  }
}

class BorderPolygon extends Polygon {
  constructor(points: XY[]) {
    super(points, {
      left: CROP_CANVAS_CORNER_SIZE / 2,
      top: CROP_CANVAS_CORNER_SIZE / 2,
      fill: "rgba(0,0,0,0)",
      strokeWidth: CROP_CANVAS_RESIZER_WIDTH,
      stroke: "#19e3ff",
      objectCaching: false,
      transparentCorners: false,
      lockMovementX: true,
      lockMovementY: true,
      selectable: false,
      hoverCursor: "default",
      hasBorders: false,
    });
  }
}

// --- Types ---

export interface CroppedImage {
  id: string;
  blob: Blob;
}

interface Cell {
  lt: XY;
  rt: XY;
  lb: XY;
  rb: XY;
}

type PointType = keyof typeof CROP_CANVAS_POINT_TYPES;

interface SplitPoint extends XY {
  type: PointType;
}

interface StepSplitProps {
  imageUrl: string;
  resetSignal: number;
  onSplitComplete: (images: CroppedImage[]) => void;
  splitRef: React.MutableRefObject<(() => Promise<void>) | null>;
}

// --- Helpers ---

const CANVAS_MAX_WIDTH = 1000;
const CANVAS_MAX_HEIGHT = 600;

const checkMouseOnLine = (point: XY, line: SplitLine) => {
  const { x: pX, y: pY } = point;
  let { x1 = 0, x2 = 0, y1 = 0, y2 = 0 } = line;

  if (line.direction === "vertical") {
    if (y1 > y2) [y1, y2] = [y2, y1];
    return pY > y1 && pY < y2 && pX > x1 - CROP_CANVAS_MOUSE_BUFFER && pX < x1 + CROP_CANVAS_MOUSE_BUFFER;
  } else {
    if (x1 > x2) [x1, x2] = [x2, x1];
    return pX > x1 && pX < x2 && pY > y1 - CROP_CANVAS_MOUSE_BUFFER && pY < y1 + CROP_CANVAS_MOUSE_BUFFER;
  }
};

const getClosestLine = (point: XY, lines: SplitLine[]) => {
  return lines.filter((line) => checkMouseOnLine(point, line))[0];
};

let idCounter = 0;
const generateId = () => `mc-${++idCounter}-${Date.now()}`;

// --- Component ---

export function StepSplit({ imageUrl, resetSignal, onSplitComplete, splitRef }: StepSplitProps) {
  const [fabricTargetCanvas, setFabricTargetCanvas] = useState<HTMLCanvasElement | null>(null);
  const [fabricCanvas, setFabricCanvas] = useState<Canvas | null>(null);

  const borderPolygonRef = useRef<BorderPolygon | null>(null);
  const edgeLineShapesRef = useRef<SplitLine[]>([]);
  const cuttingLineShapesRef = useRef<SplitLine[]>([]);
  const prevPointRef = useRef<XY>();
  const isMouseOnLineShapeRef = useRef(false);
  const tempLineShapeRef = useRef<SplitLine>();
  const pointerCircleRef = useRef<PointerCircle>();

  const containerRef = useRef<HTMLDivElement>(null);

  const getContainedSize = useCallback((imgW: number, imgH: number) => {
    // 컨테이너 실측 크기 사용 — 고정 상수 대신 실제 가용 공간에 맞춤
    const maxW = containerRef.current?.clientWidth ?? CANVAS_MAX_WIDTH;
    const maxH = containerRef.current?.clientHeight ?? CANVAS_MAX_HEIGHT;
    const ratio = imgW / imgH;
    if (ratio > maxW / maxH) {
      return { width: maxW, height: maxW / ratio };
    }
    return { width: maxH * ratio, height: maxH };
  }, []);


  const drawPointer = useCallback(
    ({ top, left }: { top: number; left: number }) => {
      if (!fabricCanvas) return;

      if (top > fabricCanvas.height - CROP_CANVAS_CORNER_SIZE)
        top = fabricCanvas.height - CROP_CANVAS_CORNER_SIZE;
      if (left > fabricCanvas.width - CROP_CANVAS_CORNER_SIZE)
        left = fabricCanvas.width - CROP_CANVAS_CORNER_SIZE;

      if (prevPointRef.current) {
        const { x: prevX, y: prevY } = prevPointRef.current;
        if (tempLineShapeRef.current) {
          tempLineShapeRef.current.set({ x2: left, y2: top });
        } else {
          const line = new SplitLine(
            [prevX, prevY, left, top],
            prevX === left ? "vertical" : "horizontal",
          );
          tempLineShapeRef.current = line;
          fabricCanvas.add(line);
        }
      }

      if (pointerCircleRef.current) {
        pointerCircleRef.current.set({ top, left });
      } else {
        const circle = new PointerCircle(top, left);
        pointerCircleRef.current = circle;
        fabricCanvas.add(circle);
      }

      fabricCanvas.renderAll();
    },
    [fabricCanvas],
  );

  const createImageCanvas = useCallback(
    async (cell: Cell) => {
      const imgEl = (fabricCanvas!.backgroundImage as FabricImage).getElement();
      const imgW = imgEl.width;
      const imgH = imgEl.height;
      const scaledW = (fabricCanvas!.backgroundImage as FabricImage).getScaledWidth();
      const scaledH = (fabricCanvas!.backgroundImage as FabricImage).getScaledHeight();
      const scaleX = imgW / scaledW;
      const scaleY = imgH / scaledH;
      const halfR = CROP_CANVAS_RESIZER_WIDTH / 2;

      const srcX = (cell.lt.x - halfR) * scaleX;
      const srcY = (cell.lt.y - halfR) * scaleY;
      const srcW = (cell.rt.x - cell.lt.x - CROP_CANVAS_RESIZER_WIDTH) * scaleX;
      const srcH = (cell.rb.y - cell.rt.y - CROP_CANVAS_RESIZER_WIDTH) * scaleY;

      const canvas = document.createElement("canvas");
      canvas.width = srcW;
      canvas.height = srcH;
      const ctx = canvas.getContext("2d")!;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(imgEl, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
      return canvas;
    },
    [fabricCanvas],
  );

  const createBlobFromCell = useCallback(
    async (cell: Cell) => {
      const canvas = await createImageCanvas(cell);
      return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Cannot convert to blob"))),
          "image/png",
          1,
        );
      });
    },
    [createImageCanvas],
  );

  const getPointsFromLines = (lineShapes: SplitLine[]) => {
    const points: SplitPoint[][] = [];
    const verticals = lineShapes.filter((l) => l.direction === "vertical").toSorted((a, b) => a.x1 - b.x1);
    const horizontals = lineShapes.filter((l) => l.direction === "horizontal").toSorted((a, b) => a.y1 - b.y1);

    for (const hLine of horizontals) {
      const row: SplitPoint[] = [];
      const { x1: hX1, x2: hX2, y1: hY } = hLine;

      for (const vLine of verticals) {
        const { x1: vX, y1: vY1, y2: vY2 } = vLine;
        const l = hX1 < vX && hX2 >= vX && vY1 <= hY && vY2 >= hY ? "l" : "";
        const t = vY1 < hY && vY2 >= hY && hX1 <= vX && hX2 >= vX ? "t" : "";
        const r = hX2 > vX && hX1 <= vX && vY1 <= hY && vY2 >= hY ? "r" : "";
        const b = vY2 > hY && vY1 <= hY && hX1 <= vX && hX2 >= vX ? "b" : "";
        const pointType = l + t + r + b;
        if (pointType.length >= 2) {
          row.push({
            x: vX - CROP_CANVAS_CORNER_SIZE / 2,
            y: hY - CROP_CANVAS_CORNER_SIZE / 2,
            type: pointType as PointType,
          });
        }
      }
      points.push(row);
    }
    return points;
  };

  const getCellsFromPoints = (points: SplitPoint[][]) => {
    const cells: Cell[] = [];
    const ltTypes: number[] = [CROP_CANVAS_POINT_TYPES.trb, CROP_CANVAS_POINT_TYPES.lrb, CROP_CANVAS_POINT_TYPES.ltrb, CROP_CANVAS_POINT_TYPES.rb];
    const rtTypes: number[] = [CROP_CANVAS_POINT_TYPES.lrb, CROP_CANVAS_POINT_TYPES.ltb, CROP_CANVAS_POINT_TYPES.ltrb, CROP_CANVAS_POINT_TYPES.lb];
    const lbTypes: number[] = [CROP_CANVAS_POINT_TYPES.ltr, CROP_CANVAS_POINT_TYPES.trb, CROP_CANVAS_POINT_TYPES.ltrb, CROP_CANVAS_POINT_TYPES.tr];

    for (let i = 0; i < points.length - 1; i++) {
      for (let j = 0; j < points[i].length - 1; j++) {
        const lt = points[i][j];
        if (!ltTypes.includes(CROP_CANVAS_POINT_TYPES[lt.type])) continue;

        const lb = points.slice(i + 1).flat().find(
          (p) => p.x === lt.x && lbTypes.includes(CROP_CANVAS_POINT_TYPES[p.type]),
        );
        const rt = points[i].slice(j + 1).find(
          (p) => p.y === lt.y && rtTypes.includes(CROP_CANVAS_POINT_TYPES[p.type]),
        );

        if (lb && rt) {
          cells.push({ lt, rt, lb, rb: { x: rt.x, y: lb.y } });
        }
      }
    }
    return cells;
  };

  const handleConfirm = useCallback(async () => {
    const allLines = [...edgeLineShapesRef.current, ...cuttingLineShapesRef.current];
    const points = getPointsFromLines(allLines);
    const cells = getCellsFromPoints(points);

    const images = await Promise.all(
      cells.map(async (cell) => {
        const blob = await createBlobFromCell(cell);
        return { id: generateId(), blob };
      }),
    );
    onSplitComplete(images);
  }, [createBlobFromCell, onSplitComplete]);

  useEffect(() => {
    splitRef.current = handleConfirm;
  }, [handleConfirm, splitRef]);

  const resetLines = useCallback(() => {
    if (!fabricCanvas) return;
    for (const line of cuttingLineShapesRef.current) {
      fabricCanvas.remove(line);
    }
    cuttingLineShapesRef.current = [];
    if (pointerCircleRef.current) {
      fabricCanvas.remove(pointerCircleRef.current);
      pointerCircleRef.current = undefined;
    }
    if (tempLineShapeRef.current) {
      fabricCanvas.remove(tempLineShapeRef.current);
      tempLineShapeRef.current = undefined;
    }
    prevPointRef.current = undefined;
    fabricCanvas.renderAll();
  }, [fabricCanvas]);

  useEffect(() => {
    resetLines();
  }, [resetSignal, resetLines]);

  const getMousePosition = useCallback(
    (event: MouseEvent) => {
      if (!fabricCanvas) return { x: 0, y: 0 };
      const rect = fabricCanvas.getElement().getBoundingClientRect();
      return { x: event.clientX - rect.x, y: event.clientY - rect.y };
    },
    [fabricCanvas],
  );

  // 이미지 로드 후 정확한 크기로 캔버스를 처음부터 생성 (리사이즈 불필요)
  useEffect(() => {
    if (!fabricTargetCanvas || fabricCanvas || !imageUrl) return;

    FabricImage.fromURL(imageUrl, { crossOrigin: "anonymous" }).then((fabricImage) => {
      const { width, height } = getContainedSize(fabricImage.width, fabricImage.height);

      if (width === CANVAS_MAX_WIDTH) {
        fabricImage.scaleToWidth(width - CROP_CANVAS_CORNER_SIZE - CROP_CANVAS_RESIZER_WIDTH);
      } else {
        fabricImage.scaleToHeight(height - CROP_CANVAS_CORNER_SIZE - CROP_CANVAS_RESIZER_WIDTH);
      }

      const canvasW = fabricImage.getScaledWidth() + CROP_CANVAS_CORNER_SIZE + CROP_CANVAS_RESIZER_WIDTH * 2;
      const canvasH = fabricImage.getScaledHeight() + CROP_CANVAS_CORNER_SIZE + CROP_CANVAS_RESIZER_WIDTH * 2;

      const canvas = new Canvas(fabricTargetCanvas, {
        backgroundColor: "#1b1c1f",
        selection: false,
        width: canvasW,
        height: canvasH,
      });

      const resizerCell: Cell = {
        lt: { x: 0, y: 0 },
        lb: { x: 0, y: fabricImage.getScaledHeight() + CROP_CANVAS_RESIZER_WIDTH },
        rb: {
          x: fabricImage.getScaledWidth() + CROP_CANVAS_RESIZER_WIDTH,
          y: fabricImage.getScaledHeight() + CROP_CANVAS_RESIZER_WIDTH,
        },
        rt: { x: fabricImage.getScaledWidth() + CROP_CANVAS_RESIZER_WIDTH, y: 0 },
      };

      fabricImage.set("top", (CROP_CANVAS_CORNER_SIZE + CROP_CANVAS_RESIZER_WIDTH) / 2);
      fabricImage.set("left", (CROP_CANVAS_CORNER_SIZE + CROP_CANVAS_RESIZER_WIDTH) / 2);
      fabricImage.set("originX", "left");
      fabricImage.set("originY", "top");
      fabricImage.set("hasBorders", false);
      canvas.backgroundImage = fabricImage;

      // Border polygon
      const border = new BorderPolygon([resizerCell.lt, resizerCell.lb, resizerCell.rb, resizerCell.rt]);
      borderPolygonRef.current = border;
      canvas.add(border);

      // Edge lines
      const r = CROP_CANVAS_CORNER_SIZE / 2;
      const ew = canvasW - r;
      const eh = canvasH - r;
      edgeLineShapesRef.current = [
        new SplitLine([r, r, ew, r], "horizontal"),
        new SplitLine([r, eh, ew, eh], "horizontal"),
        new SplitLine([r, r, r, eh], "vertical"),
        new SplitLine([ew, r, ew, eh], "vertical"),
      ];

      canvas.renderAll();
      setFabricCanvas(canvas);
    });
  }, [fabricTargetCanvas, fabricCanvas, imageUrl, getContainedSize]);

  // Mouse event listeners
  useEffect(() => {
    if (!fabricCanvas) return;

    const onMouseMove = (event: TPointerEventInfo) => {
      const edges = edgeLineShapesRef.current;
      const mousePos = getMousePosition(event.e as MouseEvent);

      if (
        mousePos.x > fabricCanvas.width - CROP_CANVAS_CORNER_SIZE / 2 ||
        mousePos.y > fabricCanvas.height - CROP_CANVAS_CORNER_SIZE / 2
      ) {
        return;
      }

      drawPointer({ top: mousePos.y, left: mousePos.x });

      const closestToMouse = getClosestLine(mousePos, edges);
      if (
        closestToMouse &&
        !(
          prevPointRef.current &&
          (checkMouseOnLine(prevPointRef.current, closestToMouse) ||
            closestToMouse?.direction !== getClosestLine(prevPointRef.current, edges)?.direction)
        )
      ) {
        isMouseOnLineShapeRef.current = true;
        fabricCanvas.hoverCursor = "crosshair";
        fabricCanvas.setCursor("crosshair");
      } else {
        isMouseOnLineShapeRef.current = false;
        fabricCanvas.hoverCursor = "not-allowed";
        fabricCanvas.setCursor("not-allowed");
      }
    };

    const onMouseUp = (event: TPointerEventInfo) => {
      if (!isMouseOnLineShapeRef.current) return;

      const edges = edgeLineShapesRef.current;
      let { x: currentX, y: currentY } = getMousePosition(event.e as MouseEvent);
      const closestLine = getClosestLine({ x: currentX, y: currentY }, edges);
      if (!closestLine) return;

      if (closestLine.direction === "horizontal") {
        currentY = closestLine.y1;
      } else {
        currentX = closestLine.x1;
      }

      if (prevPointRef.current) {
        const { x: prevX, y: prevY } = prevPointRef.current;
        if (Math.abs(currentY - prevY) > Math.abs(currentX - prevX)) {
          currentX = prevX;
        } else {
          currentY = prevY;
        }

        const lineShape = new SplitLine(
          [prevX, prevY, currentX, currentY],
          prevX === currentX ? "vertical" : "horizontal",
        );
        cuttingLineShapesRef.current.push(lineShape);
        fabricCanvas.add(lineShape);

        if (tempLineShapeRef.current) {
          fabricCanvas.remove(tempLineShapeRef.current);
          tempLineShapeRef.current = undefined;
        }
        prevPointRef.current = undefined;
      } else {
        prevPointRef.current = { x: currentX, y: currentY };
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape") {
        if (pointerCircleRef.current) fabricCanvas.remove(pointerCircleRef.current);
        if (tempLineShapeRef.current) fabricCanvas.remove(tempLineShapeRef.current);
        prevPointRef.current = undefined;
        tempLineShapeRef.current = undefined;
        pointerCircleRef.current = undefined;
        fabricCanvas.renderAll();
      } else if ((e.metaKey || e.ctrlKey) && e.key === "z" && !e.shiftKey) {
        // 입력 중인 곳에서는 무시
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
        e.preventDefault();
        // 그리는 중이면 그리기 취소(Escape와 동일), 아니면 마지막 분할선 제거
        if (prevPointRef.current) {
          if (pointerCircleRef.current) fabricCanvas.remove(pointerCircleRef.current);
          if (tempLineShapeRef.current) fabricCanvas.remove(tempLineShapeRef.current);
          prevPointRef.current = undefined;
          tempLineShapeRef.current = undefined;
          pointerCircleRef.current = undefined;
          fabricCanvas.renderAll();
        } else {
          const lastLine = cuttingLineShapesRef.current.pop();
          if (lastLine) {
            fabricCanvas.remove(lastLine);
            fabricCanvas.renderAll();
          }
        }
      }
    };

    fabricCanvas.on("mouse:move", onMouseMove);
    fabricCanvas.on("mouse:up", onMouseUp);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      fabricCanvas.off("mouse:move", onMouseMove);
      fabricCanvas.off("mouse:up", onMouseUp);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [drawPointer, fabricCanvas, getMousePosition, handleConfirm]);

  return (
    <Container ref={containerRef}>
      <CanvasWrapper>
        <canvas ref={setFabricTargetCanvas} />
      </CanvasWrapper>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
  background: #1b1c1f;
  border-radius: 8px;
  overflow: hidden;
`;

const CanvasWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
